import { Controller } from "@hotwired/stimulus"

/**
 * Form Select Controller
 *
 * A custom single-select dropdown that works with dark mode styling.
 * Replaces native <select> elements with a styled dropdown menu.
 *
 * Usage:
 *   <div data-controller="form-select" data-form-select-name-value="field_name">
 *     <button type="button" data-form-select-target="trigger" data-action="click->form-select#toggle">
 *       <span data-form-select-target="display">Select...</span>
 *     </button>
 *     <div data-form-select-target="dropdown" class="hidden">
 *       <button type="button" data-action="click->form-select#select" data-value="option1">Option 1</button>
 *     </div>
 *     <input type="hidden" data-form-select-target="input" name="field_name">
 *   </div>
 */
export default class extends Controller {
  static targets = ["trigger", "dropdown", "display", "input", "option"]
  static values = {
    name: String,
    selected: { type: String, default: "" },
    placeholder: { type: String, default: "Select..." }
  }

  connect() {
    // Initialize with current value if set
    if (this.hasInputTarget && this.inputTarget.value) {
      this.selectedValue = this.inputTarget.value
      this.updateDisplay()
    }

    // Close dropdown when clicking outside
    this.clickOutsideHandler = this.clickOutside.bind(this)
    document.addEventListener("click", this.clickOutsideHandler)

    // Handle keyboard navigation
    this.keydownHandler = this.handleKeydown.bind(this)
    this.element.addEventListener("keydown", this.keydownHandler)
  }

  disconnect() {
    document.removeEventListener("click", this.clickOutsideHandler)
    this.element.removeEventListener("keydown", this.keydownHandler)
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.isOpen()) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    if (!this.hasDropdownTarget) return

    this.dropdownTarget.classList.remove("hidden", "opacity-0", "scale-95")
    this.dropdownTarget.classList.add("opacity-100", "scale-100")

    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute("aria-expanded", "true")
    }

    // Highlight currently selected option
    this.highlightSelected()
  }

  close() {
    if (!this.hasDropdownTarget) return

    this.dropdownTarget.classList.add("hidden", "opacity-0", "scale-95")
    this.dropdownTarget.classList.remove("opacity-100", "scale-100")

    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute("aria-expanded", "false")
    }
  }

  isOpen() {
    return this.hasDropdownTarget && !this.dropdownTarget.classList.contains("hidden")
  }

  select(event) {
    event.preventDefault()
    event.stopPropagation()

    const button = event.currentTarget
    const value = button.dataset.value
    const label = button.dataset.label || button.textContent.trim()

    this.selectedValue = value

    if (this.hasInputTarget) {
      this.inputTarget.value = value
      // Dispatch change event for form validation
      this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }

    this.updateDisplayText(label)
    this.highlightSelected()
    this.close()
  }

  updateDisplay() {
    if (!this.selectedValue) {
      this.updateDisplayText(this.placeholderValue)
      return
    }

    // Find the option with this value and get its label
    const selectedOption = this.optionTargets.find(opt => opt.dataset.value === this.selectedValue)
    if (selectedOption) {
      const label = selectedOption.dataset.label || selectedOption.textContent.trim()
      this.updateDisplayText(label)
    }
  }

  updateDisplayText(text) {
    if (this.hasDisplayTarget) {
      this.displayTarget.textContent = text

      // Update styling based on whether a value is selected
      if (this.selectedValue) {
        this.displayTarget.classList.remove("text-gray-500")
        this.displayTarget.classList.add("text-white")
      } else {
        this.displayTarget.classList.add("text-gray-500")
        this.displayTarget.classList.remove("text-white")
      }
    }
  }

  highlightSelected() {
    this.optionTargets.forEach(option => {
      const isSelected = option.dataset.value === this.selectedValue

      if (isSelected) {
        option.classList.add("bg-emerald-900/50", "text-emerald-300")
        option.classList.remove("text-gray-300")
      } else {
        option.classList.remove("bg-emerald-900/50", "text-emerald-300")
        option.classList.add("text-gray-300")
      }
    })
  }

  clickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  handleKeydown(event) {
    if (!this.isOpen()) {
      if (event.key === "Enter" || event.key === " " || event.key === "ArrowDown") {
        event.preventDefault()
        this.open()
      }
      return
    }

    switch (event.key) {
      case "Escape":
        event.preventDefault()
        this.close()
        this.triggerTarget?.focus()
        break
      case "ArrowDown":
        event.preventDefault()
        this.focusNextOption()
        break
      case "ArrowUp":
        event.preventDefault()
        this.focusPreviousOption()
        break
      case "Enter":
      case " ":
        event.preventDefault()
        const focused = document.activeElement
        if (this.optionTargets.includes(focused)) {
          focused.click()
        }
        break
    }
  }

  focusNextOption() {
    const current = document.activeElement
    const index = this.optionTargets.indexOf(current)
    const next = this.optionTargets[index + 1] || this.optionTargets[0]
    next?.focus()
  }

  focusPreviousOption() {
    const current = document.activeElement
    const index = this.optionTargets.indexOf(current)
    const prev = this.optionTargets[index - 1] || this.optionTargets[this.optionTargets.length - 1]
    prev?.focus()
  }
}
